import { useState } from 'react';
import Icon from '../design/Icon';
import { inr, fmtTime, fmtDateShort, todayStr, addDaysStr } from '../design/atoms';

/* A coach, therapist or consultant as members see them — in the checkout
   coach step, the gallery, and (read-only) the admin's live preview. Photo
   first; tap flips to bio and certifications. */

export interface CoachCardData {
  id: string;
  name: string;
  photoUrl: string | null;
  headline: string | null;
  bio?: string | null;
  specialties: string[];
  certifications?: Array<{ name: string; issuer?: string | null; year?: number | null }>;
  experienceYears: number | null;
  languages: string[];
  level: { name: string; tone: string } | null;
  nextFree?: { date: string; startTime: string } | null;
}

/** `today` is the gym's date (from the API); the device date is only a fallback. */
export function nextFreeLabel(nf: { date: string; startTime: string }, today: string = todayStr()): string {
  const day = nf.date === today ? 'Today' : nf.date === addDaysStr(today, 1) ? 'Tomorrow' : fmtDateShort(nf.date);
  return `${day} ${fmtTime(nf.startTime)}`;
}

export function coachMeta(c: Pick<CoachCardData, 'experienceYears' | 'languages'>): string {
  return [c.experienceYears ? `${c.experienceYears} yr${c.experienceYears === 1 ? '' : 's'}` : null, c.languages.slice(0, 3).join(', ') || null]
    .filter(Boolean)
    .join(' · ');
}

export default function CoachCard({ coach, today, price, priceSuffix = '', selected, onSelect, actionLabel, flippable = true }: {
  coach: CoachCardData;
  today?: string;
  /** Shown as "from ₹X" / "₹X" under the chips. */
  price?: { amount: number; from?: boolean } | null;
  priceSuffix?: string;
  selected?: boolean;
  onSelect?: () => void;
  actionLabel?: string;
  flippable?: boolean;
}) {
  const [flipped, setFlipped] = useState(false);
  const meta = coachMeta(coach);
  const hasBack = flippable && !!(coach.bio || coach.certifications?.length);

  return (
    <div className={`coach${flipped ? ' flip' : ''}${selected ? ' on' : ''}`}>
      <div className="coach-in">
        <div className="coach-face coach-front">
          <button
            type="button"
            className="coach-photo"
            style={{ width: '100%', display: 'flex' }}
            onClick={() => hasBack && setFlipped(true)}
            aria-label={hasBack ? `About ${coach.name}` : coach.name}
          >
            {coach.photoUrl ? <img src={coach.photoUrl} alt="" /> : <span className="initial">{coach.name.charAt(0).toUpperCase()}</span>}
            <span className="scrim" />
            <span className="coach-top">
              {coach.level ? <span className={`lvl ${coach.level.tone}`}>{coach.level.name}</span> : <span />}
              {coach.nextFree && <span className="free"><i />Next free: {nextFreeLabel(coach.nextFree, today)}</span>}
            </span>
            <span className="over">
              <span className="dp dp-m">{coach.name}</span>
              {coach.headline && <span className="sub">{coach.headline}</span>}
              {meta && <span className="sub">{meta}</span>}
            </span>
          </button>
          <div className="coach-body">
            {coach.specialties.length > 0 && (
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {coach.specialties.slice(0, 4).map(s => <span key={s} className="tag">{s}</span>)}
              </div>
            )}
            {(price || onSelect) && (
              <div className="between">
                {price ? (
                  <span className="mid num">{price.from ? 'from ' : ''}{inr(price.amount)}<span className="tiny">{priceSuffix}</span></span>
                ) : <span />}
                {onSelect && (
                  <button type="button" className={`btn sm ${selected ? '' : 'gold'}`} onClick={onSelect}>
                    {selected ? <><Icon n="check" s={15} /> Chosen</> : actionLabel ?? 'Choose'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {hasBack && (
          <div className="coach-face coach-back">
            <div className="between">
              <div className="dp dp-m trunc">{coach.name}</div>
              <button type="button" className="icon-btn" onClick={() => setFlipped(false)} aria-label="Back to photo"><Icon n="x" s={17} /></button>
            </div>
            {coach.bio && <div className="sub wrap" style={{ whiteSpace: 'pre-line' }}>{coach.bio}</div>}
            {!!coach.certifications?.length && (
              <div className="col" style={{ gap: 8 }}>
                <span className="lbl">Certifications</span>
                {coach.certifications.map((c, i) => (
                  <div key={i} className="cert">
                    <span className="li gold"><Icon n="medal" s={16} /></span>
                    <div className="col" style={{ minWidth: 0 }}>
                      <span className="mid trunc">{c.name}</span>
                      {(c.issuer || c.year) && <span className="tiny trunc">{[c.issuer, c.year].filter(Boolean).join(' · ')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {onSelect && (
              <button type="button" className={`btn block ${selected ? '' : 'gold'}`} style={{ marginTop: 'auto' }} onClick={onSelect}>
                {selected ? 'Chosen' : actionLabel ?? `Choose ${coach.name.split(' ')[0]}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The "Any available" option — first in the rail when the offering allows it. */
export function AnyCoachCard({ price, selected, onSelect }: { price: number; selected: boolean; onSelect: () => void }) {
  return (
    <button type="button" className={`coach-any${selected ? ' on' : ''}`} onClick={onSelect}>
      <span className="li gold" style={{ width: 44, height: 44, flex: '0 0 44px' }}><Icon n="spark" s={20} /></span>
      <span className="dp dp-m">Any available</span>
      <span className="sub wrap">Book whoever is free, each time. Best for flexible schedules.</span>
      <span className="between" style={{ marginTop: 6 }}>
        <span className="mid num">{inr(price)}</span>
        <span className={`btn sm ${selected ? '' : 'ghost'}`}>{selected ? <><Icon n="check" s={15} /> Chosen</> : 'Choose'}</span>
      </span>
    </button>
  );
}
